import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { techAPI, formsAPI } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { SignaturePad } from '../../components/SignaturePad';
import { evaluateVisibility } from '../../lib/formVisibility';
import { groupFieldsIntoSections } from '../../lib/formSections';
import { toast } from 'sonner';
import {
  ArrowLeft, Navigation, Phone, MessageSquare, MapPin, Clock,
  Camera, FileText, ClipboardCheck, Package, Loader2,
  CheckCircle2, Truck, Send, AlertTriangle,
  DollarSign, User, ChevronDown, ChevronUp, Image,
  X, Plus, Minus, Upload, Search, Pencil, Receipt, CreditCard,
  Trash2, RotateCcw, Lock,
} from 'lucide-react';
import CardPaymentModal from './CardPaymentModal';

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', bg: 'bg-slate-100', text: 'text-slate-700' },
  en_route:  { label: 'En Route',  bg: 'bg-blue-100',  text: 'text-blue-800'  },
  on_site:   { label: 'On Site',   bg: 'bg-amber-100', text: 'text-amber-900' },
  done:      { label: 'Complete',  bg: 'bg-emerald-100', text: 'text-emerald-800' },
};

const WORKFLOW_ACTIONS = {
  scheduled: { label: 'On My Way',  icon: Truck,        next: 'en_route', color: 'bg-blue-600 active:bg-blue-700 text-white' },
  en_route:  { label: 'Arrived',    icon: MapPin,        next: 'on_site',  color: 'bg-amber-500 active:bg-amber-600 text-slate-950' },
  on_site:   { label: 'Finish Job', icon: CheckCircle2,  next: 'done',     color: 'bg-emerald-600 active:bg-emerald-700 text-white' },
};

/* ─── CLOSEOUT MODAL ────────────────────────────────────────────── */
function CloseoutModal({ eventId, onConfirm, onCancel }) {
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [finalNote, setFinalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    techAPI.getCloseoutCheck(eventId).then(r => {
      setCheck(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [eventId]);

  const doFinish = async () => {
    setSubmitting(true);
    try {
      if (finalNote.trim()) {
        await techAPI.addNote(eventId, `[CLOSEOUT] ${finalNote.trim()}`, 'internal');
      }
      await onConfirm();
    } catch {
      toast.error('Failed to finish job');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
        </div>
      </div>
    );
  }

  const steps = [
    { title: 'Review', key: 'review' },
    { title: 'Final Note', key: 'note' },
    { title: 'Confirm', key: 'confirm' },
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="closeout-modal">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
        {/* Header */}
        <div className="bg-emerald-600 px-5 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-white">Finish Job</h3>
          <button onClick={onCancel} className="p-1 rounded-full text-white/80 active:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1 px-5 pt-4 pb-2">
          {steps.map((s, i) => (
            <div key={s.key} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              <p className={`text-[9px] mt-1 font-bold uppercase tracking-wide ${i <= step ? 'text-emerald-600' : 'text-slate-300'}`}>{s.title}</p>
            </div>
          ))}
        </div>

        <div className="px-5 pb-5 pt-2 min-h-[220px]">
          {/* Step 0: Review */}
          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 mb-3">Before finishing, review your documentation:</p>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                <span className="flex items-center gap-2 text-sm text-slate-700"><FileText className="w-4 h-4 text-slate-400" /> Notes</span>
                <Badge className={check?.notes_count > 0 ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                  {check?.notes_count || 0} entries
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                <span className="flex items-center gap-2 text-sm text-slate-700"><Camera className="w-4 h-4 text-slate-400" /> Photos</span>
                <Badge className={check?.photos_count > 0 ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                  {check?.photos_count || 0} photos
                </Badge>
              </div>
              {check?.checklist_total > 0 && (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <span className="flex items-center gap-2 text-sm text-slate-700"><ClipboardCheck className="w-4 h-4 text-slate-400" /> Checklist</span>
                  <Badge className={check?.checklist_completed === check?.checklist_total ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                    {check?.checklist_completed}/{check?.checklist_total} done
                  </Badge>
                </div>
              )}
              {check?.has_invoice && (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <span className="flex items-center gap-2 text-sm text-slate-700"><DollarSign className="w-4 h-4 text-slate-400" /> Invoice</span>
                  <Badge className={check?.invoice_status === 'paid' ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                    {check?.invoice_status}
                  </Badge>
                </div>
              )}
              <button onClick={() => setStep(1)} className="w-full h-12 bg-emerald-600 text-white font-bold rounded-lg active:bg-emerald-700 mt-2" data-testid="closeout-next">
                Continue
              </button>
            </div>
          )}

          {/* Step 1: Final note */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 mb-1">Any final notes before closing?</p>
              <textarea
                value={finalNote}
                onChange={e => setFinalNote(e.target.value)}
                placeholder="e.g., Replaced capacitor, system running normally. Recommended follow-up in 6 months."
                className="w-full h-28 p-3 text-sm border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none resize-none"
                data-testid="closeout-note"
              />
              <div className="flex gap-2">
                <button onClick={() => setStep(0)} className="flex-1 h-12 border-2 border-slate-200 text-slate-600 font-bold rounded-lg active:bg-slate-50">Back</button>
                <button onClick={() => setStep(2)} className="flex-1 h-12 bg-emerald-600 text-white font-bold rounded-lg active:bg-emerald-700" data-testid="closeout-to-confirm">
                  {finalNote.trim() ? 'Save & Continue' : 'Skip'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Confirm */}
          {step === 2 && (
            <div className="space-y-4 text-center pt-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <div>
                <p className="text-lg font-bold text-slate-950">Mark Job Complete?</p>
                <p className="text-sm text-slate-500 mt-1">This will notify the office and close out the visit.</p>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setStep(1)} className="flex-1 h-12 border-2 border-slate-200 text-slate-600 font-bold rounded-lg active:bg-slate-50">Back</button>
                <button
                  onClick={doFinish}
                  disabled={submitting}
                  className="flex-1 h-14 bg-emerald-600 text-white font-bold rounded-lg active:bg-emerald-700 disabled:opacity-60"
                  data-testid="closeout-confirm"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Finish Job'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── NOTES SECTION ─────────────────────────────────────────────── */
function NoteSection({ legacyNotes, eventId }) {
  const [text, setText] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState('internal');

  useEffect(() => {
    if (!loaded) {
      techAPI.getNotes(eventId).then(r => {
        setNotes(r.data || []);
        setLoaded(true);
      }).catch(() => setLoaded(true));
    }
  }, [eventId, loaded]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await techAPI.addNote(eventId, text.trim(), noteType);
      setNotes(prev => [...prev, res.data.note]);
      setText('');
      toast.success(`${noteType === 'customer' ? 'Customer' : 'Internal'} note saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (noteId) => {
    setDeletingId(noteId);
    try {
      await techAPI.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = notes.filter(n => n.note_type === activeTab);

  return (
    <div className="border-t-2 border-slate-100" data-testid="notes-section">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
          <FileText className="w-4 h-4 text-slate-500" /> Notes
          {notes.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{notes.length}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Tab switcher */}
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('internal')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-bold transition-colors ${activeTab === 'internal' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-500'}`}
              data-testid="notes-tab-internal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Internal
              {notes.filter(n => n.note_type === 'internal').length > 0 && (
                <span className={`text-[10px] px-1.5 rounded-full ${activeTab === 'internal' ? 'bg-white/30' : 'bg-slate-200'}`}>
                  {notes.filter(n => n.note_type === 'internal').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('customer')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-bold transition-colors ${activeTab === 'customer' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500'}`}
              data-testid="notes-tab-customer"
            >
              <User className="w-3.5 h-3.5" />
              Customer
              {notes.filter(n => n.note_type === 'customer').length > 0 && (
                <span className={`text-[10px] px-1.5 rounded-full ${activeTab === 'customer' ? 'bg-white/30' : 'bg-slate-200'}`}>
                  {notes.filter(n => n.note_type === 'customer').length}
                </span>
              )}
            </button>
          </div>

          {/* Notes list */}
          {!loaded ? (
            <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : filtered.length > 0 ? (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {filtered.map(n => (
                <div key={n.id}
                  className={`p-3 rounded-lg border ${n.note_type === 'internal'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-blue-50 border-blue-200'
                  }`}
                  data-testid={`note-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed flex-1">{n.content}</p>
                    <button
                      onClick={() => handleDelete(n.id)}
                      disabled={deletingId === n.id}
                      className="p-1 text-slate-300 hover:text-red-400 shrink-0"
                      data-testid={`note-delete-${n.id}`}
                    >
                      {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-slate-400">{n.author_name}</span>
                    <span className="text-[10px] text-slate-300">&middot;</span>
                    <span className="text-[10px] text-slate-400">
                      {n.created_at ? new Date(n.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-3 text-center">
              <p className="text-xs text-slate-400">No {activeTab} notes yet</p>
            </div>
          )}

          {/* Add note form */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setNoteType('internal')}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${noteType === 'internal'
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'border-slate-200 text-slate-400'
                }`}
                data-testid="note-type-internal"
              >Internal</button>
              <button
                onClick={() => setNoteType('customer')}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${noteType === 'customer'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-slate-200 text-slate-400'
                }`}
                data-testid="note-type-customer"
              >Customer</button>
            </div>
            <div className="flex gap-2">
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={noteType === 'internal' ? 'Internal note...' : 'Customer-visible note...'}
                className={`flex-1 h-11 px-3 text-sm border-2 rounded-lg focus:outline-none bg-white ${
                  noteType === 'internal' ? 'border-amber-200 focus:border-amber-400' : 'border-blue-200 focus:border-blue-400'
                }`}
                onKeyDown={e => e.key === 'Enter' && submit()}
                data-testid="note-input"
              />
              <button onClick={submit} disabled={sending || !text.trim()}
                className={`h-11 w-11 flex items-center justify-center rounded-lg text-white disabled:opacity-40 ${
                  noteType === 'internal' ? 'bg-amber-500 active:bg-amber-600' : 'bg-blue-600 active:bg-blue-700'
                }`}
                data-testid="note-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Legacy notes block */}
          {legacyNotes && (
            <div className="border-t border-dashed border-slate-200 pt-3">
              <button
                onClick={() => setShowLegacy(!showLegacy)}
                className="flex items-center gap-2 text-xs text-slate-400 font-medium"
                data-testid="legacy-notes-toggle"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
                Legacy Notes (read-only)
                {showLegacy ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showLegacy && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 max-h-40 overflow-y-auto">
                  <p className="text-xs text-slate-500 whitespace-pre-wrap leading-relaxed font-mono">{legacyNotes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── PHOTOS SECTION ────────────────────────────────────────────── */
function PhotosSection({ attachments, eventId, onPhotoAdded, onCaptionUpdate }) {
  const [expanded, setExpanded] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [editingCaption, setEditingCaption] = useState(null); // { id, caption }
  const [savingCaption, setSavingCaption] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Photo exceeds 15MB limit');
      return;
    }
    setUploading(true);
    setProgress('Uploading...');
    try {
      const res = await techAPI.uploadPhoto(eventId, file);
      onPhotoAdded(res.data);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  const saveCaption = async () => {
    if (!editingCaption) return;
    setSavingCaption(true);
    try {
      await techAPI.updatePhotoCaption(eventId, editingCaption.id, editingCaption.caption);
      onCaptionUpdate(editingCaption.id, editingCaption.caption);
      setEditingCaption(null);
      toast.success('Caption updated');
    } catch (err) {
      toast.error('Failed to save caption');
    } finally {
      setSavingCaption(false);
    }
  };

  return (
    <div className="border-t-2 border-slate-100" data-testid="photos-section">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
          <Camera className="w-4 h-4 text-slate-500" /> Photos
          {attachments.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{attachments.length}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {/* Caption editor overlay */}
          {editingCaption && (
            <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="caption-editor">
              <div className="bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-950 flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit Caption</span>
                  <button onClick={() => setEditingCaption(null)} className="p-1 text-slate-400"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-4">
                  <input
                    value={editingCaption.caption}
                    onChange={e => setEditingCaption(prev => ({ ...prev, caption: e.target.value }))}
                    placeholder="Add a caption..."
                    className="w-full h-10 px-3 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && saveCaption()}
                    data-testid="caption-input"
                  />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setEditingCaption(null)} className="flex-1 h-10 border border-slate-200 text-slate-600 text-sm font-bold rounded-lg active:bg-slate-50">Cancel</button>
                    <button onClick={saveCaption} disabled={savingCaption}
                      className="flex-1 h-10 bg-blue-600 text-white text-sm font-bold rounded-lg active:bg-blue-700 disabled:opacity-40" data-testid="caption-save">
                      {savingCaption ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Photo grid */}
          {attachments.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {attachments.map(att => (
                <button key={att.id}
                  onClick={() => setEditingCaption({ id: att.id, caption: att.caption || '' })}
                  className="aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200 relative group"
                  data-testid={`photo-${att.id}`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`}
                    alt={att.caption || att.file_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {att.caption ? (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                      <p className="text-[9px] text-white truncate">{att.caption}</p>
                    </div>
                  ) : (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/30 px-1.5 py-0.5 flex items-center justify-center">
                      <Pencil className="w-3 h-3 text-white/70" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {attachments.length === 0 && !uploading && (
            <div className="py-4 text-center">
              <Image className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No photos yet</p>
            </div>
          )}

          {uploading && (
            <div className="flex items-center justify-center gap-2 py-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <span className="text-sm text-slate-500">{progress}</span>
            </div>
          )}

          {/* Upload buttons */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-blue-300 text-sm font-bold text-blue-600 active:bg-blue-50 disabled:opacity-40"
              data-testid="take-photo-btn"
            >
              <Camera className="w-4 h-4" /> Take Photo
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-slate-300 text-sm font-bold text-slate-500 active:bg-slate-50 disabled:opacity-40"
              data-testid="upload-photo-btn"
            >
              <Upload className="w-4 h-4" /> Gallery
            </button>
          </div>

          {/* Hidden file inputs */}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => handleUpload(e.target.files?.[0])} />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={e => handleUpload(e.target.files?.[0])} />
        </div>
      )}
    </div>
  );
}

/* ─── CHECKLIST SECTION ─────────────────────────────────────────── */
function _resolveFileUrl(url) {
  if (!url) return '';
  if (/^https?:/i.test(url)) return url;
  return (process.env.REACT_APP_BACKEND_URL || '') + url;
}

/** Touch-tuned file uploader for the tech mobile checklist.
 *  Locked when the submission is no longer a draft. */
function MobileFileField({ submissionId, field, value, locked, onChange, uploadParams }) {
  const files = Array.isArray(value) ? value : [];
  const [busy, setBusy] = useState(false);

  const upload = async (fileList) => {
    if (locked || fileList.length === 0) return;
    setBusy(true);
    let added = 0;
    for (const f of fileList) {
      try {
        const res = await formsAPI.uploadSubmissionFile(submissionId, field.key, f, uploadParams || null);
        added += 1;
        // optimistic local update so the tile appears immediately
        onChange([...(files || []), res.data.file]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    if (added > 0) toast.success(`${added} file${added === 1 ? '' : 's'} uploaded`);
    setBusy(false);
  };

  const remove = async (file) => {
    if (locked) return;
    try {
      await formsAPI.deleteSubmissionFile(submissionId, field.key, file.id, uploadParams || null);
      onChange(files.filter(f => f.id !== file.id));
      toast.success('File removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <div className="px-3 py-3 space-y-2" data-testid={`mobile-file-${field.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-700 font-medium">
          {field.label}{field.is_required && <span className="text-rose-500 ml-0.5">*</span>}
        </p>
        {locked && <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> locked</span>}
      </div>

      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5">
          {files.map(f => {
            const isImg = f.file_type === 'image';
            const url = _resolveFileUrl(f.file_url);
            return (
              <div key={f.id} className="relative rounded-md border border-slate-200 bg-white overflow-hidden" data-testid={`mobile-file-tile-${f.id}`}>
                {isImg ? (
                  <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={f.file_name} className="w-full h-20 object-cover" />
                  </a>
                ) : (
                  <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-20 bg-slate-50 text-slate-600 px-1">
                    <FileText className="w-5 h-5 text-rose-500" />
                    <span className="text-[9px] mt-0.5 line-clamp-2 text-center">{f.file_name}</span>
                  </a>
                )}
                {!locked && (
                  <button
                    type="button"
                    onClick={() => remove(f)}
                    className="absolute top-1 right-1 bg-white/95 border border-slate-200 rounded-full p-1 text-rose-500 active:bg-rose-50 shadow-sm"
                    data-testid={`mobile-file-delete-${f.id}`}
                    aria-label={`Remove ${f.file_name}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!locked && (
        <label
          className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 active:bg-slate-50 text-sm font-medium text-slate-600 px-4 py-3 ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          data-testid={`mobile-file-upload-${field.key}`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          <span>{busy ? 'Uploading…' : (files.length === 0 ? 'Add Photo or PDF' : 'Add Another')}</span>
          <input
            type="file"
            multiple
            accept="image/*,application/pdf"
            capture="environment"
            className="hidden"
            disabled={busy}
            onChange={async (e) => {
              const fl = Array.from(e.target.files || []);
              if (fl.length === 0) return;
              await upload(fl);
              e.target.value = '';
            }}
          />
        </label>
      )}

      {field.is_required && files.length === 0 && !locked && (
        <p className="text-[11px] text-amber-600">Required — upload at least one file.</p>
      )}
    </div>
  );
}

/** Touch-tuned signature capture for the tech mobile checklist. */
function MobileSignatureField({ field, value, locked, onChange }) {
  const v = value || {};
  const isSigned = !!v.signature_data_url;
  const [signerName, setSignerName] = useState(v.signer_name || '');
  const [reSigning, setReSigning] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSignerName(v.signer_name || ''); /* eslint-disable-next-line */ }, [v.signer_name]);

  const onPad = async (dataUrl) => {
    if (locked) return;
    if (!dataUrl) return;  // ignore "clear" until user confirms via Re-sign
    if (!signerName.trim()) {
      toast.error('Enter signer name first');
      return;
    }
    setSaving(true);
    try {
      await onChange({
        signer_name: signerName.trim(),
        signature_data_url: dataUrl,
        signed_at: new Date().toISOString(),
      });
      setReSigning(false);
    } finally {
      setSaving(false);
    }
  };

  if (isSigned && !reSigning) {
    return (
      <div className="px-3 py-3 space-y-2" data-testid={`mobile-sig-${field.key}`}>
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-700 font-medium">
            {field.label}{field.is_required && <span className="text-rose-500 ml-0.5">*</span>}
          </p>
          {locked && <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> locked</span>}
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2">
          <div className="text-xs">
            <p className="font-medium text-emerald-900">Signed by {v.signer_name || '—'}</p>
            {v.signed_at && <p className="text-emerald-700/80">{new Date(v.signed_at).toLocaleString()}</p>}
          </div>
          <img src={v.signature_data_url} alt={`Signature of ${v.signer_name}`} className="bg-white border border-emerald-200 rounded-md max-h-20" />
          {!locked && (
            <button
              type="button"
              onClick={() => setReSigning(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 active:text-emerald-900"
              data-testid={`mobile-sig-resign-${field.key}`}
            >
              <RotateCcw className="w-3 h-3" /> Re-sign
            </button>
          )}
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="px-3 py-3" data-testid={`mobile-sig-${field.key}`}>
        <p className="text-sm text-slate-700 font-medium">
          {field.label}{field.is_required && <span className="text-rose-500 ml-0.5">*</span>}
        </p>
        <p className="text-xs text-slate-400 italic mt-1">Not signed (form locked)</p>
      </div>
    );
  }

  // Active sign / re-sign
  return (
    <div className="px-3 py-3 space-y-2" data-testid={`mobile-sig-${field.key}`}>
      <p className="text-sm text-slate-700 font-medium">
        {field.label}{field.is_required && <span className="text-rose-500 ml-0.5">*</span>}
      </p>
      <input
        type="text"
        placeholder="Signer name"
        value={signerName}
        onChange={(e) => setSignerName(e.target.value)}
        className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
        data-testid={`mobile-sig-name-${field.key}`}
      />
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <SignaturePad
          onSignatureChange={onPad}
          width={Math.min(360, window.innerWidth - 60)}
          height={140}
          disabled={!signerName.trim() || saving}
        />
      </div>
      {!signerName.trim() && (
        <p className="text-[11px] text-amber-600">Enter signer name before drawing the signature.</p>
      )}
      {field.is_required && !signerName.trim() && (
        <p className="text-[11px] text-amber-600">Required — sign to complete this field.</p>
      )}
    </div>
  );
}

// ─── Phase 3 — Repeating Sections (Tech mobile) ────────────────────────────
/**
 * Renders a single field row in the tech mobile checklist.
 * Optional (sectionKey, instanceIndex) wires saves into a repeatable instance.
 */
function MobileChecklistField({
  f, value, cl, eventId, isLocked, toggling, toggleItem,
  onChecklistUpdate, sectionKey = null, instanceIndex = null,
}) {
  const isInstance = sectionKey !== null && instanceIndex !== null;
  const opts = isInstance ? { section_key: sectionKey, instance_index: instanceIndex } : {};
  const localOnChange = (newVal) => {
    onChecklistUpdate(cl.id, f.key, newVal, isInstance ? { sectionKey, instanceIndex } : null);
  };

  if (f.field_type === 'static') {
    const md = f?.config?.content_markdown || '';
    return (
      <div className="px-3 py-2.5 text-xs text-slate-600 whitespace-pre-wrap bg-slate-50/50" data-testid={`checklist-static-${f.key}`}>
        {md}
      </div>
    );
  }
  if (f.field_type === 'file') {
    return (
      <MobileFileField
        submissionId={cl.id}
        field={f}
        value={value}
        locked={isLocked}
        uploadParams={isInstance ? { section_key: sectionKey, instance_index: instanceIndex } : null}
        onChange={localOnChange}
      />
    );
  }
  if (f.field_type === 'signature') {
    return (
      <MobileSignatureField
        field={f}
        value={value}
        locked={isLocked}
        onChange={async (newVal) => {
          try {
            await techAPI.updateChecklist(eventId, cl.id, f.key, newVal, opts);
            localOnChange(newVal);
            toast.success('Signature saved');
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save signature');
            throw err;
          }
        }}
      />
    );
  }
  const isCheckbox = f.field_type === 'checkbox';
  const isLoading = toggling === `${cl.id}:${isInstance ? `${sectionKey}:${instanceIndex}:` : ''}${f.key}`;
  return (
    <button
      onClick={isCheckbox && !isLocked ? () => toggleItem(cl.id, f.key, value, isInstance ? { sectionKey, instanceIndex } : null) : undefined}
      disabled={isLoading || !isCheckbox || isLocked}
      className={`w-full flex items-center gap-3 px-3 py-3 text-left ${isCheckbox && !isLocked ? 'active:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
      data-testid={isInstance ? `checklist-item-${sectionKey}-${instanceIndex}-${f.key}` : `checklist-item-${f.key}`}
    >
      {isCheckbox ? (
        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${value ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'} ${isLocked ? 'opacity-60' : ''}`}>
          {isLoading ? <Loader2 className="w-3 h-3 animate-spin text-white" /> :
            value && <CheckCircle2 className="w-4 h-4 text-white" />}
        </div>
      ) : (
        <div className="w-6 h-6 rounded-md border-2 border-slate-200 flex items-center justify-center shrink-0">
          <FileText className="w-3 h-3 text-slate-300" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <span className={`text-sm ${value && isCheckbox ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{f.label}</span>
        {!isCheckbox && value !== null && value !== undefined && value !== '' && (
          <p className="text-xs text-slate-400 truncate mt-0.5">{Array.isArray(value) ? value.join(', ') : String(value)}</p>
        )}
      </div>
    </button>
  );
}

/**
 * Renders a single section group: either a flat list of fields, or, for
 * repeatable sections, one instance block per entry in
 * `rootData[section_key]` with an "Add another …" button at the bottom.
 */
function MobileChecklistGroup({
  group, cl, eventId, rootData, isLocked, toggling, toggleItem, onChecklistUpdate,
}) {
  const section = group.section;
  // Phase 2.x: when a section_header has a visible_if rule that evaluates
  // false against the current root data, hide the whole group.
  if (section && !evaluateVisibility({ ...section, field_key: section.key }, rootData, [])) {
    return null;
  }
  const headerNode = section ? (
    <div className="px-3 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-testid={`checklist-section-${section.key}`}>
      {section.label}
      {group.repeatable && (
        <span className="ml-2 text-[10px] text-violet-600 normal-case font-medium">↻ repeats</span>
      )}
    </div>
  ) : null;

  // Non-repeatable: render fields with values from rootData (top-level).
  if (!group.repeatable) {
    const visible = group.fields
      .filter(f => f.field_type !== 'divider')
      .filter(f => evaluateVisibility(f, rootData, group.fields));
    return (
      <>
        {headerNode}
        {visible.map(f => (
          <MobileChecklistField
            key={f.key} f={f} value={rootData[f.key]}
            cl={cl} eventId={eventId} isLocked={isLocked}
            toggling={toggling} toggleItem={toggleItem}
            onChecklistUpdate={onChecklistUpdate}
          />
        ))}
      </>
    );
  }

  // Repeatable: each instance gets its own data scope.
  const sectionKey = section.key;
  const rawInstances = Array.isArray(rootData[sectionKey]) ? rootData[sectionKey] : [];
  const instances = rawInstances.length > 0 ? rawInstances : [{}];
  const max = group.max || 20;
  const canAdd = !isLocked && instances.length < max;
  const canRemove = !isLocked && instances.length > 1;

  const addInstance = async () => {
    const next = [...instances, {}];
    try {
      await techAPI.updateChecklist(eventId, cl.id, sectionKey, next);
      onChecklistUpdate(cl.id, sectionKey, next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add instance');
    }
  };
  const removeInstance = async (idx) => {
    const next = instances.filter((_, i) => i !== idx);
    try {
      await techAPI.updateChecklist(eventId, cl.id, sectionKey, next);
      onChecklistUpdate(cl.id, sectionKey, next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove instance');
    }
  };

  return (
    <>
      {headerNode}
      {instances.map((inst, idx) => {
        // Per-instance visibility: merge root + instance scope so visible_if
        // can reference fields outside the section as well.
        const instData = inst || {};
        const merged = { ...rootData, ...instData };
        const visible = group.fields
          .filter(f => f.field_type !== 'divider')
          .filter(f => evaluateVisibility(f, merged, group.fields));
        return (
          <div
            key={`${sectionKey}-${idx}`}
            className="px-3 py-2 bg-violet-50/30 border-l-4 border-violet-300"
            data-testid={`tech-repeat-instance-${sectionKey}-${idx}`}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-bold text-violet-900 uppercase tracking-wide">
                {group.label || 'Instance'} {idx + 1}
              </p>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => removeInstance(idx)}
                  className="text-rose-500 active:text-rose-700 inline-flex items-center gap-1 text-[10px]"
                  data-testid={`tech-repeat-remove-${sectionKey}-${idx}`}
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
            <div className="divide-y divide-violet-100 -mx-3">
              {visible.map(f => (
                <MobileChecklistField
                  key={`${sectionKey}-${idx}-${f.key}`}
                  f={f}
                  value={instData[f.key]}
                  cl={cl} eventId={eventId} isLocked={isLocked}
                  toggling={toggling} toggleItem={toggleItem}
                  onChecklistUpdate={onChecklistUpdate}
                  sectionKey={sectionKey} instanceIndex={idx}
                />
              ))}
            </div>
          </div>
        );
      })}
      {canAdd && (
        <button
          type="button"
          onClick={addInstance}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-[12px] font-medium text-violet-700 active:bg-violet-50"
          data-testid={`tech-repeat-add-${sectionKey}`}
        >
          <Plus className="w-3.5 h-3.5" /> Add another {group.label || 'instance'}
        </button>
      )}
    </>
  );
}

function ChecklistSection({ checklists, eventId, onChecklistUpdate, onChecklistAttached }) {
  const [expanded, setExpanded] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [showAttach, setShowAttach] = useState(false);
  const [available, setAvailable] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [attaching, setAttaching] = useState(null);

  const toggleItem = async (submissionId, fieldKey, currentValue, scope = null) => {
    const newValue = !currentValue;
    const togglingKey = scope
      ? `${submissionId}:${scope.sectionKey}:${scope.instanceIndex}:${fieldKey}`
      : `${submissionId}:${fieldKey}`;
    setToggling(togglingKey);
    try {
      await techAPI.updateChecklist(eventId, submissionId, fieldKey, newValue, scope ? { section_key: scope.sectionKey, instance_index: scope.instanceIndex } : {});
      onChecklistUpdate(submissionId, fieldKey, newValue, scope);
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setToggling(null);
    }
  };

  const openAttach = async () => {
    setShowAttach(true);
    setLoadingAvail(true);
    try {
      const res = await techAPI.getAvailableChecklists(eventId);
      setAvailable(res.data || []);
    } catch { setAvailable([]); }
    finally { setLoadingAvail(false); }
  };

  const attachTemplate = async (templateId) => {
    setAttaching(templateId);
    try {
      const res = await techAPI.attachChecklist(eventId, templateId);
      toast.success('Checklist attached');
      onChecklistAttached(res.data.checklist);
      setAvailable(prev => prev.filter(t => t.id !== templateId));
      if (available.length <= 1) setShowAttach(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to attach');
    } finally {
      setAttaching(null);
    }
  };

  return (
    <div className="border-t-2 border-slate-100" data-testid="checklist-section">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
          <ClipboardCheck className="w-4 h-4 text-slate-500" /> Checklists
          {checklists.length > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{checklists.length}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {checklists.length > 0 ? (
            <div className="space-y-3">
              {checklists.map(cl => {
                const checkboxFields = cl.fields.filter(f => f.field_type === 'checkbox');
                const completed = checkboxFields.filter(f => f.value).length;
                // Phase 2: hide fields whose `visible_if` rule is not satisfied.
                // The mobile checklist payload uses `f.key` instead of `field_key`;
                // build a normalized fields list + data dict from the same source.
                const dataForVis = {};
                cl.fields.forEach(f => { dataForVis[f.key] = f.value; });
                const fieldsForVis = cl.fields.map(f => ({ ...f, field_key: f.key }));
                const renderableFields = fieldsForVis
                  .filter(f => f.field_type !== 'divider')
                  .filter(f => evaluateVisibility(f, dataForVis, fieldsForVis));
                // Phase 1b.1: lock the checklist UI when the submission is no
                // longer a draft (e.g. submitted/locked) OR when an active
                // customer signature link is outstanding. The backend rejects
                // edits in both cases — this just keeps the UI honest.
                const isLocked = (cl.status || 'draft') !== 'draft' || !!cl.has_active_customer_action;
                // Phase 3: group fields into sections so repeatable section
                // headers can clone their child fields per-instance.
                const groups = groupFieldsIntoSections(fieldsForVis);
                const rootData = cl.data || dataForVis;
                return (
                  <div key={cl.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`checklist-block-${cl.id}`}>
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                      <p className="text-xs font-bold text-slate-700">{cl.template_name}</p>
                      <div className="flex items-center gap-1.5">
                        {cl.has_active_customer_action && (cl.status || 'draft') === 'draft' && (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px] inline-flex items-center gap-0.5" data-testid={`checklist-pending-customer-${cl.id}`}>
                            <Lock className="w-2.5 h-2.5" /> awaiting customer
                          </Badge>
                        )}
                        {cl.status && cl.status !== 'draft' && (
                          <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid={`checklist-status-${cl.id}`}>{cl.status}</Badge>
                        )}
                        {checkboxFields.length > 0 && (
                          <Badge className={completed === checkboxFields.length ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}>
                            {completed}/{checkboxFields.length}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {groups.map((group, gi) => (
                        <MobileChecklistGroup
                          key={group.section ? group.section.key : `g-${gi}`}
                          group={group}
                          cl={cl}
                          eventId={eventId}
                          rootData={rootData}
                          isLocked={isLocked}
                          toggling={toggling}
                          toggleItem={toggleItem}
                          onChecklistUpdate={onChecklistUpdate}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-3 text-center">
              <ClipboardCheck className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No checklists assigned</p>
            </div>
          )}

          {/* Attach checklist */}
          {showAttach ? (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid="attach-checklist-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-slate-600">Available Checklists</p>
                <button onClick={() => setShowAttach(false)} className="p-1 text-slate-400"><X className="w-4 h-4" /></button>
              </div>
              {loadingAvail ? (
                <div className="flex justify-center py-2"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : available.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">All checklists are already attached</p>
              ) : (
                <div className="space-y-1.5">
                  {available.map(t => (
                    <button key={t.id} onClick={() => attachTemplate(t.id)}
                      disabled={attaching === t.id}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-white rounded-lg border border-slate-200 active:bg-blue-50 disabled:opacity-50"
                      data-testid={`attach-template-${t.id}`}>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-slate-700 truncate">{t.name}</p>
                        {t.category && <p className="text-[10px] text-slate-400">{t.category}</p>}
                      </div>
                      {attaching === t.id
                        ? <Loader2 className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
                        : <Plus className="w-4 h-4 text-blue-500 shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button onClick={openAttach}
              className="w-full flex items-center justify-center gap-2 h-10 mt-3 border-2 border-dashed border-slate-300 rounded-lg text-sm font-bold text-slate-500 active:bg-slate-50"
              data-testid="attach-checklist-btn">
              <Plus className="w-4 h-4" /> Attach Checklist
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── CREATE INVOICE MODAL ──────────────────────────────────────── */
function CreateInvoiceModal({ eventId, onCreated, onCancel }) {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (!showSearch || searchQuery.length < 1) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await techAPI.searchPriceBook(searchQuery);
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, showSearch]);

  const addItem = (pbItem) => {
    setItems(prev => [...prev, {
      id: Date.now(),
      name: pbItem.name,
      type: pbItem.item_type || 'service',
      quantity: 1,
      unit_price: pbItem.unit_price || 0,
      taxable: pbItem.taxable !== false,
      price_book_item_id: pbItem.id,
    }]);
    setShowSearch(false);
    setSearchQuery('');
  };

  const addCustomItem = () => {
    setItems(prev => [...prev, {
      id: Date.now(),
      name: '',
      type: 'service',
      quantity: 1,
      unit_price: 0,
      taxable: true,
      isCustom: true,
    }]);
    setShowSearch(false);
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const subtotal = items.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0);

  const submit = async () => {
    if (items.length === 0) { toast.error('Add at least one line item'); return; }
    const invalidItems = items.filter(i => !i.name.trim() || i.unit_price <= 0);
    if (invalidItems.length > 0) { toast.error('Fill in all item names and prices'); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map(i => ({
          name: i.name,
          type: i.type,
          quantity: i.quantity,
          unit_price: i.unit_price,
          taxable: i.taxable,
          price_book_item_id: i.price_book_item_id || undefined,
        })),
      };
      const res = await techAPI.createInvoice(eventId, payload);
      setSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="create-invoice-modal">
      <div className="bg-white w-full max-w-md max-h-[90vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col">
        <div className="bg-slate-900 px-5 py-4 flex items-center justify-between shrink-0">
          <h3 className="text-base font-bold text-white flex items-center gap-2"><Receipt className="w-4 h-4" /> Create Invoice</h3>
          <button onClick={onCancel} className="p-1 rounded-full text-white/80 active:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        {success ? (
          <div className="px-5 py-8 text-center" data-testid="invoice-create-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-slate-950">Invoice Created</p>
            <p className="text-sm text-slate-500 mt-1">#{success.invoice_number}</p>
            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-mono font-bold">${success.subtotal.toFixed(2)}</span></div>
              {success.tax_amount > 0 && (
                <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Tax</span><span className="font-mono">${success.tax_amount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-sm font-bold mt-1 pt-1 border-t border-slate-200"><span>Total</span><span className="font-mono">${success.total.toFixed(2)}</span></div>
            </div>
            <button onClick={() => onCreated(success)}
              className="w-full h-12 mt-4 bg-slate-900 text-white font-bold rounded-lg active:bg-slate-800" data-testid="invoice-create-done">
              Done
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-4">
              {items.length > 0 && (
                <div className="space-y-2 mb-4">
                  {items.map(item => (
                    <div key={item.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="invoice-line-item">
                      {item.isCustom ? (
                        <input value={item.name} onChange={e => updateItem(item.id, 'name', e.target.value)}
                          placeholder="Item name" className="w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white focus:border-blue-500 focus:outline-none mb-2"
                          autoFocus data-testid="custom-item-name" />
                      ) : (
                        <p className="text-sm font-bold text-slate-800 mb-2 pr-6">{item.name}</p>
                      )}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateItem(item.id, 'quantity', Math.max(0.5, item.quantity - 0.5))}
                            className="w-7 h-7 flex items-center justify-center rounded border border-slate-300 active:bg-slate-100"><Minus className="w-3 h-3" /></button>
                          <span className="w-8 text-center text-xs font-mono font-bold">{item.quantity}</span>
                          <button onClick={() => updateItem(item.id, 'quantity', item.quantity + 0.5)}
                            className="w-7 h-7 flex items-center justify-center rounded border border-slate-300 active:bg-slate-100"><Plus className="w-3 h-3" /></button>
                        </div>
                        <span className="text-xs text-slate-400">x</span>
                        <div className="flex items-center h-8 border border-slate-200 rounded overflow-hidden flex-1 focus-within:border-blue-500">
                          <span className="px-1.5 text-xs text-slate-400">$</span>
                          <input type="number" step="0.01" min="0" value={item.unit_price}
                            onChange={e => updateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="flex-1 h-full text-sm font-mono font-bold text-slate-950 focus:outline-none pr-2 w-16" data-testid="item-price" />
                        </div>
                        <span className="text-sm font-mono font-bold text-slate-700 w-16 text-right">${(item.quantity * item.unit_price).toFixed(2)}</span>
                        <button onClick={() => removeItem(item.id)} className="p-1 text-red-400 active:text-red-600"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showSearch ? (
                <div className="mb-4">
                  <div className="flex items-center gap-2 h-10 border-2 border-blue-300 rounded-lg overflow-hidden bg-white">
                    <Search className="w-4 h-4 text-slate-400 ml-3" />
                    <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search Price Book..." className="flex-1 h-full text-sm focus:outline-none"
                      autoFocus data-testid="price-book-search" />
                    <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="px-2 text-slate-400"><X className="w-4 h-4" /></button>
                  </div>
                  {searching && <div className="flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /><span className="text-xs text-slate-400">Searching...</span></div>}
                  {searchResults.length > 0 && (
                    <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                      {searchResults.map(r => (
                        <button key={r.id} onClick={() => addItem(r)}
                          className="w-full flex items-center justify-between px-3 py-2.5 text-left border-b border-slate-100 last:border-0 active:bg-blue-50"
                          data-testid={`pb-result-${r.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                            <p className="text-[10px] text-slate-400">{r.item_type}{r.sku ? ` · ${r.sku}` : ''}</p>
                          </div>
                          <span className="text-sm font-mono font-bold text-slate-600 ml-2">${r.unit_price.toFixed(2)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={addCustomItem}
                    className="w-full flex items-center justify-center gap-1.5 h-9 mt-2 text-xs font-bold text-blue-600 border border-dashed border-blue-300 rounded-lg active:bg-blue-50"
                    data-testid="add-custom-item">
                    <Plus className="w-3 h-3" /> Custom Line Item
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowSearch(true)}
                  className="w-full flex items-center justify-center gap-2 h-12 border-2 border-dashed border-slate-300 rounded-lg text-sm font-bold text-slate-500 active:bg-slate-50 mb-4"
                  data-testid="add-invoice-item-btn">
                  <Plus className="w-4 h-4" /> Add Line Item
                </button>
              )}

              {items.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal ({items.length} items)</span>
                    <span className="font-mono font-bold text-slate-950">${subtotal.toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Tax calculated automatically based on tenant settings</p>
                </div>
              )}

              <button onClick={submit} disabled={submitting || items.length === 0}
                className="w-full h-14 bg-slate-900 text-white font-bold rounded-lg active:bg-slate-800 disabled:opacity-40"
                data-testid="create-invoice-submit">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Create Invoice · $${subtotal.toFixed(2)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── COLLECT PAYMENT MODAL ─────────────────────────────────────── */
function CollectPaymentModal({ invoice, eventId, onPaymentRecorded, onCancel, initialMethod, allowedMethods }) {
  const [method, setMethod] = useState(initialMethod || null);
  const [amount, setAmount] = useState(String(invoice.balance_due || 0));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  const am = allowedMethods || { cash: true, check: true };
  const METHODS = [
    am.cash !== false && { key: 'cash', label: 'Cash', icon: DollarSign },
    am.check !== false && { key: 'check', label: 'Check', icon: FileText },
    { key: 'other', label: 'Other', icon: FileText },
  ].filter(Boolean);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > invoice.balance_due) { toast.error(`Amount exceeds balance due ($${invoice.balance_due.toFixed(2)})`); return; }
    setSubmitting(true);
    try {
      const res = await techAPI.collectPayment(eventId, {
        amount: amt,
        payment_method: method,
        reference_number: reference || undefined,
        notes: notes || undefined,
        invoice_id: invoice.id,
      });
      setResult(res.data);
      setSuccess(true);
      onPaymentRecorded(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment recording failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="payment-modal">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden">
        <div className="bg-blue-600 px-5 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-white">Record Payment</h3>
          <button onClick={onCancel} className="p-1 rounded-full text-white/80 active:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4">
          {success ? (
            <div className="text-center py-4" data-testid="payment-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-lg font-bold text-slate-950">Payment Recorded</p>
              <p className="text-sm text-slate-500 mt-1">${result?.amount?.toFixed(2)} via {method}</p>
              {result?.invoice_balance_due > 0 && (
                <p className="text-sm text-amber-600 mt-1">Remaining balance: ${result.invoice_balance_due.toFixed(2)}</p>
              )}
              {result?.invoice_balance_due <= 0 && (
                <p className="text-sm text-emerald-600 mt-1 font-bold">Invoice fully paid</p>
              )}
              <button onClick={onCancel} className="w-full h-12 mt-4 bg-slate-100 text-slate-700 font-bold rounded-lg active:bg-slate-200" data-testid="payment-done">Done</button>
            </div>
          ) : !method ? (
            <>
              <p className="text-xs text-slate-400 mb-1">Invoice #{invoice.invoice_number}</p>
              <p className="text-lg font-bold font-mono text-slate-950 mb-4">Balance Due: ${invoice.balance_due.toFixed(2)}</p>
              <p className="text-sm text-slate-600 mb-3">How was payment received?</p>
              <p className="text-[10px] text-slate-400 mb-3">This records a payment collected in the field. It does not process a card transaction.</p>
              <div className="space-y-2">
                {METHODS.map(m => (
                  <button key={m.key} onClick={() => setMethod(m.key)}
                    className="w-full flex items-center gap-3 h-14 px-4 rounded-lg border-2 border-slate-200 active:border-blue-400 active:bg-blue-50"
                    data-testid={`payment-method-${m.key}`}>
                    <m.icon className="w-5 h-5 text-slate-500" />
                    <span className="text-base font-bold text-slate-700">{m.label}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <button onClick={() => setMethod(null)} className="text-xs text-blue-600 font-bold">&larr; Back</button>
                <span className="text-xs text-slate-400">Recording {method} payment</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 mb-1 block">Amount</label>
                  <div className="flex items-center h-12 border-2 border-slate-200 rounded-lg overflow-hidden focus-within:border-blue-500">
                    <span className="px-3 text-slate-400 font-bold">$</span>
                    <input type="number" step="0.01" min="0.01" max={invoice.balance_due}
                      value={amount} onChange={e => setAmount(e.target.value)}
                      className="flex-1 h-full text-lg font-mono font-bold text-slate-950 focus:outline-none pr-3"
                      data-testid="payment-amount" />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Full balance: ${invoice.balance_due.toFixed(2)}
                    {parseFloat(amount) > 0 && parseFloat(amount) < invoice.balance_due && (
                      <span className="text-amber-500 ml-1">(partial payment)</span>
                    )}
                  </p>
                </div>
                {method === 'check' && (
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-500 mb-1 block">Check Number</label>
                    <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Check #"
                      className="w-full h-10 px-3 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      data-testid="payment-reference" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-500 mb-1 block">Notes (optional)</label>
                  <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g., Received at door"
                    className="w-full h-10 px-3 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    data-testid="payment-notes" />
                </div>
                <button onClick={submit} disabled={submitting}
                  className="w-full h-14 bg-blue-600 text-white font-bold rounded-lg active:bg-blue-700 disabled:opacity-60 mt-2"
                  data-testid="payment-submit">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Record $${parseFloat(amount || 0).toFixed(2)} Payment`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── INVOICE SECTION (Multi-Invoice, Technician-Safe) ──────────── */
function InvoiceSection({ invoices, eventId, customerPhone, onInvoicesChange, onInvoiceCreated }) {
  const [expanded, setExpanded] = useState(true);
  const [activeInvoiceId, setActiveInvoiceId] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showCardPayment, setShowCardPayment] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [sendingLink, setSendingLink] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');

  const activeInvoice = invoices.find(i => i.id === activeInvoiceId);

  const statusConfig = {
    draft:          { label: 'Draft',   dot: 'bg-slate-400',  bg: 'bg-slate-50',  text: 'text-slate-600',  border: 'border-slate-200' },
    sent:           { label: 'Sent',    dot: 'bg-blue-500',   bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200' },
    paid:           { label: 'Paid',    dot: 'bg-emerald-500',bg: 'bg-emerald-50', text: 'text-emerald-700',border: 'border-emerald-200' },
    partially_paid: { label: 'Partial', dot: 'bg-amber-500',  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200' },
    partial:        { label: 'Partial', dot: 'bg-amber-500',  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200' },
    overdue:        { label: 'Overdue', dot: 'bg-red-500',    bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200' },
  };
  const fallbackCfg = { label: 'Unknown', dot: 'bg-slate-400', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };

  const handlePaymentRecorded = (result) => {
    onInvoicesChange(prev => prev.map(inv =>
      inv.id === activeInvoiceId
        ? { ...inv, balance_due: result.invoice_balance_due, status: result.invoice_status }
        : inv
    ));
  };

  const handleSendPaymentLink = async (invoice) => {
    if (sendingLink) return;
    setSendingLink(invoice.id);
    try {
      await techAPI.sendPaymentLink(invoice.id);
      toast.success('Payment link sent to customer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send payment link');
    } finally {
      setSendingLink(null);
    }
  };

  const handleCreateConfirm = () => {
    if (invoices.length > 0) {
      toast('This job already has invoice(s). Creating another will add a separate invoice.', { duration: 3000 });
    }
    setShowCreate(true);
  };

  return (
    <div className="border-t-2 border-slate-100" data-testid="invoice-section">
      {/* Payment modals */}
      {showPayment && activeInvoice && (
        <CollectPaymentModal
          invoice={activeInvoice}
          eventId={eventId}
          initialMethod={paymentMethod}
          allowedMethods={activeInvoice.allowed_payment_methods}
          onPaymentRecorded={handlePaymentRecorded}
          onCancel={() => { setShowPayment(false); setActiveInvoiceId(null); }}
        />
      )}
      {showCardPayment && activeInvoice && (
        <CardPaymentModal
          invoice={activeInvoice}
          onSuccess={handlePaymentRecorded}
          onCancel={() => { setShowCardPayment(false); setActiveInvoiceId(null); }}
        />
      )}
      {showCreate && (
        <CreateInvoiceModal
          eventId={eventId}
          onCreated={(inv) => {
            setShowCreate(false);
            onInvoiceCreated(inv);
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
          <DollarSign className="w-4 h-4 text-slate-500" /> Invoices
          {invoices.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{invoices.length}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {invoices.length > 0 ? (
            invoices.map(inv => {
              const cfg = statusConfig[inv.status] || fallbackCfg;
              const isDraft = inv.status === 'draft';
              const hasBal = inv.balance_due > 0 && inv.status !== 'paid';
              const methods = inv.allowed_payment_methods || { card: true, cash: true, check: true, online: true };
              const createdDate = inv.created_at
                ? new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '';

              return (
                <div
                  key={inv.id}
                  className={`rounded-xl border-2 ${cfg.border} overflow-hidden`}
                  data-testid={`invoice-card-${inv.id}`}
                >
                  {/* Invoice header row */}
                  <div className={`px-4 py-3 ${cfg.bg}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`} />
                        <span className="text-sm font-bold text-slate-950 font-mono">#{inv.invoice_number}</span>
                        <Badge className={`text-[10px] font-bold uppercase ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                          {cfg.label}
                        </Badge>
                      </div>
                      {createdDate && <span className="text-[10px] text-slate-400 shrink-0">{createdDate}</span>}
                    </div>

                    {/* Amount row */}
                    <div className="flex items-baseline gap-3 mt-1.5">
                      <span className="text-base font-bold font-mono text-slate-950">${inv.total?.toFixed(2) || '0.00'}</span>
                      {hasBal && (
                        <span className="text-xs font-mono text-amber-600 font-bold">
                          Due: ${inv.balance_due.toFixed(2)}
                        </span>
                      )}
                      {inv.status === 'paid' && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 font-bold">
                          <CheckCircle2 className="w-3 h-3" /> Paid
                        </span>
                      )}
                    </div>

                    {/* Draft notice */}
                    {isDraft && (
                      <div className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 bg-white/60 rounded-lg border border-slate-200">
                        <AlertTriangle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="text-[11px] text-slate-500">Draft — office review may still be needed</span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons (only when balance is due AND method is allowed) */}
                  {hasBal && (
                    <div className="px-4 py-3 space-y-2">
                      {inv.can_charge_card && methods.card && (
                        <button
                          onClick={() => { setActiveInvoiceId(inv.id); setShowCardPayment(true); }}
                          className="w-full h-11 bg-emerald-600 text-white text-sm font-bold rounded-lg active:bg-emerald-700 flex items-center justify-center gap-2"
                          data-testid={`charge-card-${inv.id}`}
                        >
                          <CreditCard className="w-4 h-4" /> Charge Card
                        </button>
                      )}
                      {methods.cash && (
                        <button
                          onClick={() => { setActiveInvoiceId(inv.id); setShowPayment(true); setPaymentMethod('cash'); }}
                          className={`w-full h-11 text-sm font-bold rounded-lg flex items-center justify-center gap-2 ${
                            (inv.can_charge_card && methods.card)
                              ? 'border-2 border-slate-200 text-slate-600 active:bg-slate-50'
                              : 'bg-blue-600 text-white active:bg-blue-700'
                          }`}
                          data-testid={`collect-cash-${inv.id}`}
                        >
                          <DollarSign className="w-4 h-4" /> Record Cash
                        </button>
                      )}
                      {methods.check && (
                        <button
                          onClick={() => { setActiveInvoiceId(inv.id); setShowPayment(true); setPaymentMethod('check'); }}
                          className="w-full h-11 text-sm font-bold rounded-lg flex items-center justify-center gap-2 border-2 border-slate-200 text-slate-600 active:bg-slate-50"
                          data-testid={`collect-check-${inv.id}`}
                        >
                          <DollarSign className="w-4 h-4" /> Record Check
                        </button>
                      )}
                      {methods.online && customerPhone && (
                        <button
                          onClick={() => handleSendPaymentLink(inv)}
                          disabled={sendingLink === inv.id}
                          className="w-full h-10 text-sm font-bold rounded-lg flex items-center justify-center gap-2 border-2 border-slate-200 text-slate-600 active:bg-slate-50 disabled:opacity-50"
                          data-testid={`send-link-${inv.id}`}
                        >
                          {sendingLink === inv.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Send className="w-4 h-4" />}
                          {sendingLink === inv.id ? 'Sending...' : 'Text Payment Link'}
                        </button>
                      )}
                      {!methods.card && !methods.cash && !methods.check && !methods.online && (
                        <p className="text-xs text-center text-slate-400 py-1">No payment methods enabled for this invoice</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-4">
              <DollarSign className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No invoices for this job</p>
            </div>
          )}

          {/* Create invoice button */}
          <button
            onClick={handleCreateConfirm}
            className="w-full flex items-center justify-center gap-2 h-11 border-2 border-dashed border-slate-300 rounded-lg text-sm font-bold text-slate-500 active:bg-slate-50 mt-1"
            data-testid="create-invoice-btn"
          >
            <Plus className="w-4 h-4" />
            {invoices.length > 0 ? 'Create Another Invoice' : 'Create Invoice'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── ESTIMATE SECTION ──────────────────────────────────────────── */
function EstimateSection({ eventId }) {
  const [expanded, setExpanded] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const loadEstimates = () => {
    techAPI.getEstimates(eventId).then(r => {
      setEstimates(r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  };

  useEffect(() => {
    if (expanded && !loaded) {
      loadEstimates();
    }
  }, [expanded, loaded, eventId]);

  const createEstimate = async () => {
    setCreating(true);
    try {
      const res = await techAPI.createEstimate(eventId);
      toast.success(`Estimate ${res.data.estimate_number} created`);
      navigate(`/tech/estimates/${res.data.estimate_id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create estimate');
    } finally {
      setCreating(false);
    }
  };

  const EDITABLE_STATUSES = new Set(['draft', 'unscheduled', 'scheduled', 'in_progress']);

  const statusColors = {
    approved: 'bg-emerald-100 text-emerald-700',
    sent: 'bg-blue-100 text-blue-700',
    awaiting_approval: 'bg-amber-100 text-amber-800',
    declined: 'bg-red-100 text-red-700',
    draft: 'bg-slate-100 text-slate-600',
    unscheduled: 'bg-slate-100 text-slate-600',
    scheduled: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    converted_to_job: 'bg-emerald-100 text-emerald-700',
  };

  const handleEstimateClick = (est) => {
    if (EDITABLE_STATUSES.has(est.status)) {
      navigate(`/tech/estimates/${est.id}/edit`);
    } else {
      navigate(`/tech/estimates/${est.id}`);
    }
  };

  return (
    <div className="border-t-2 border-slate-100" data-testid="estimate-section">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
          <FileText className="w-4 h-4 text-slate-500" /> Estimates
          {loaded && estimates.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{estimates.length}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {!loaded ? (
            <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : estimates.length === 0 ? (
            <div className="py-3 text-center">
              <FileText className="w-7 h-7 text-slate-200 mx-auto mb-1" />
              <p className="text-xs text-slate-400">No estimates linked to this job</p>
            </div>
          ) : (
            <div className="space-y-2">
              {estimates.map(est => (
                <button key={est.id} onClick={() => handleEstimateClick(est)}
                  className="w-full text-left p-3 bg-white border border-slate-200 rounded-lg active:bg-slate-50"
                  data-testid={`estimate-link-${est.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono text-slate-400">#{est.estimate_number}</span>
                    <Badge className={`text-[10px] font-bold uppercase ${statusColors[est.status] || 'bg-slate-100 text-slate-600'}`}>
                      {est.status === 'awaiting_approval' ? 'Awaiting' : est.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm font-bold text-slate-950">{est.estimate_name || `Estimate #${est.estimate_number}`}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm font-mono font-bold text-slate-700">${est.total.toFixed(2)}</span>
                    {est.approved_option_name && (
                      <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-0.5">
                        <CheckCircle2 className="w-3 h-3" /> {est.approved_option_name}
                      </span>
                    )}
                    {est.options.length > 1 && !est.approved_option_name && (
                      <span className="text-[10px] text-slate-400">{est.options.length} options</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Create Estimate button */}
          <button onClick={createEstimate} disabled={creating}
            className="w-full flex items-center justify-center gap-2 h-10 mt-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-bold text-blue-600 active:bg-blue-50 disabled:opacity-40"
            data-testid="create-estimate-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Estimate
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── MATERIALS SECTION ─────────────────────────────────────────── */
function MaterialsSection({ materials, eventId, onMaterialAdded, onMaterialRemoved }) {
  const [expanded, setExpanded] = useState(true);
  const [mode, setMode] = useState(null); // null | 'search' | 'custom'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customName, setCustomName] = useState('');
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (mode !== 'search' || searchQuery.length < 1) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await techAPI.searchInventory(searchQuery);
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, mode]);

  const submitLinked = async (item) => {
    setSubmitting(true);
    try {
      const res = await techAPI.addMaterial(eventId, { item_id: item.id, quantity: qty, notes: notes || undefined });
      onMaterialAdded(res.data);
      resetForm();
      toast.success('Material logged');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log material');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCustom = async () => {
    if (!customName.trim()) return;
    setSubmitting(true);
    try {
      const res = await techAPI.addMaterial(eventId, { custom_name: customName.trim(), quantity: qty, notes: notes || undefined });
      onMaterialAdded(res.data);
      resetForm();
      toast.success('Material logged');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log material');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id) => {
    setDeletingId(id);
    try {
      await techAPI.deleteMaterial(id);
      onMaterialRemoved(id);
      toast.success('Material removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setDeletingId(null);
    }
  };

  const resetForm = () => {
    setMode(null);
    setSearchQuery('');
    setSearchResults([]);
    setCustomName('');
    setQty(1);
    setNotes('');
  };

  return (
    <div className="border-t-2 border-slate-100" data-testid="materials-section">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-950">
          <Package className="w-4 h-4 text-slate-500" /> Materials
          {materials.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{materials.length}</Badge>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {materials.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {materials.map((m) => (
                <div key={m.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid={`material-item-${m.id}`}>
                  <Package className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">
                      {m.quantity > 1 && <span className="font-mono text-blue-600">{m.quantity}x </span>}
                      {m.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {m.item_id && <span className="text-[10px] text-emerald-600 font-bold">Inventory</span>}
                      {!m.item_id && <span className="text-[10px] text-slate-400">Custom</span>}
                      {m.added_by_name && <span className="text-[10px] text-slate-400">&middot; {m.added_by_name}</span>}
                      {m.notes && <span className="text-[10px] text-slate-400 truncate">&middot; {m.notes}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(m.id)}
                    disabled={deletingId === m.id}
                    className="p-1 text-slate-300 hover:text-red-400 shrink-0"
                    data-testid={`material-remove-${m.id}`}
                  >
                    {deletingId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {materials.length === 0 && !mode && (
            <div className="py-3 text-center">
              <Package className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No materials logged</p>
            </div>
          )}

          {mode === 'search' && (
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 h-10 border-2 border-blue-300 rounded-lg overflow-hidden bg-white">
                <Search className="w-4 h-4 text-slate-400 ml-3" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search inventory..."
                  className="flex-1 h-full text-sm focus:outline-none"
                  autoFocus data-testid="material-search-input" />
                <button onClick={resetForm} className="px-2 text-slate-400"><X className="w-4 h-4" /></button>
              </div>
              {searching && <div className="flex items-center gap-2 py-1"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /><span className="text-xs text-slate-400">Searching...</span></div>}
              {searchResults.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-white">
                  {searchResults.map(r => (
                    <button key={r.id} onClick={() => submitLinked(r)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left border-b border-slate-100 last:border-0 active:bg-blue-50"
                      disabled={submitting}
                      data-testid={`inv-result-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                        <p className="text-[10px] text-slate-400">{r.sku || r.uom}{r.billable_price ? ` · $${r.billable_price.toFixed(2)}` : ''}</p>
                      </div>
                      <Plus className="w-4 h-4 text-blue-500 ml-2 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.length > 0 && !searching && searchResults.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-1">No items found</p>
              )}
              <button onClick={() => setMode('custom')}
                className="w-full flex items-center justify-center gap-1.5 h-9 text-xs font-bold text-blue-600 border border-dashed border-blue-300 rounded-lg active:bg-blue-50"
                data-testid="switch-to-custom-material">
                <Plus className="w-3 h-3" /> Add Custom Part (not in inventory)
              </button>
            </div>
          )}

          {mode === 'custom' && (
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Part name (e.g., 25uF Capacitor)"
                className="w-full h-10 px-3 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none bg-white"
                autoFocus data-testid="material-custom-name" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-bold">QTY</span>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-300 active:bg-slate-100"><Minus className="w-3 h-3" /></button>
                <span className="w-8 text-center font-mono font-bold text-slate-950" data-testid="material-qty">{qty}</span>
                <button onClick={() => setQty(q => q + 1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-300 active:bg-slate-100"><Plus className="w-3 h-3" /></button>
              </div>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none bg-white"
                data-testid="material-notes" />
              <div className="flex gap-2">
                <button onClick={resetForm} className="flex-1 h-10 border border-slate-200 text-slate-600 text-sm font-bold rounded-lg active:bg-slate-50">Cancel</button>
                <button onClick={submitCustom} disabled={submitting || !customName.trim()} className="flex-1 h-10 bg-blue-600 text-white text-sm font-bold rounded-lg active:bg-blue-700 disabled:opacity-40" data-testid="material-save">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Log Part'}
                </button>
              </div>
            </div>
          )}

          {!mode && (
            <button onClick={() => setMode('search')} className="w-full flex items-center justify-center gap-2 h-10 border-2 border-dashed border-slate-300 rounded-lg text-sm font-bold text-slate-500 active:bg-slate-50"
              data-testid="add-material-btn">
              <Plus className="w-4 h-4" /> Log Material Used
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── MAIN JOB DETAIL ───────────────────────────────────────────── */
export default function TechJobDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showCloseout, setShowCloseout] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detailRes, matRes] = await Promise.all([
        techAPI.getEventDetail(eventId),
        techAPI.getMaterials(eventId).catch(() => ({ data: [] })),
      ]);
      setEvent(detailRes.data);
      setInvoices(detailRes.data.invoice_summaries || (detailRes.data.invoice_summary ? [detailRes.data.invoice_summary] : []));
      setMaterials(matRes.data);
    } catch {
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const handleWorkflowAction = async (nextStatus) => {
    if (nextStatus === 'done') {
      setShowCloseout(true);
      return;
    }
    setUpdating(true);
    try {
      await techAPI.updateStatus(eventId, nextStatus);
      setEvent(prev => ({ ...prev, status: nextStatus }));
      toast.success(STATUS_CONFIG[nextStatus]?.label || nextStatus);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status update failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseoutConfirm = async () => {
    await techAPI.updateStatus(eventId, 'done');
    setEvent(prev => ({ ...prev, status: 'done' }));
    setShowCloseout(false);
    toast.success('Job complete!');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!event) {
    return (
      <div className="px-4 pt-6 text-center">
        <p className="text-slate-400">Job not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-slate-100 rounded-lg text-sm font-bold">Go Back</button>
      </div>
    );
  }

  const cust = event.customer;
  const statusCfg = STATUS_CONFIG[event.status] || STATUS_CONFIG.scheduled;
  const action = WORKFLOW_ACTIONS[event.status];
  const time = event.start_at ? new Date(event.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  const endTime = event.end_at ? new Date(event.end_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <div className="max-w-lg mx-auto pb-4" data-testid="tech-job-detail">
      {/* Closeout modal */}
      {showCloseout && (
        <CloseoutModal eventId={eventId} onConfirm={handleCloseoutConfirm} onCancel={() => setShowCloseout(false)} />
      )}

      {/* ── Sticky top bar ── */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-slate-900">
        <div className="flex items-center justify-between px-4 py-2.5">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-lg active:bg-slate-100" data-testid="job-back">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            {event.priority === 'high' && (
              <Badge className="bg-red-100 text-red-700 text-[10px] font-bold uppercase"><AlertTriangle className="w-3 h-3 mr-0.5" /> Urgent</Badge>
            )}
            <Badge className={`${statusCfg.bg} ${statusCfg.text} text-xs font-bold uppercase tracking-wide`} data-testid="job-status-badge">
              {statusCfg.label}
            </Badge>
          </div>
        </div>

        {/* Primary workflow action */}
        {action && (
          <div className="px-4 pb-3">
            <button
              onClick={() => handleWorkflowAction(action.next)}
              disabled={updating}
              className={`w-full flex items-center justify-center gap-2.5 h-14 rounded-sm text-base font-bold transition-all ${action.color} ${updating ? 'opacity-60' : ''}`}
              data-testid="workflow-action-btn"
            >
              {updating ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <><action.icon className="w-5 h-5" strokeWidth={2.5} />{action.label}</>
              )}
            </button>
          </div>
        )}

        {event.status === 'done' && (
          <div className="px-4 pb-3">
            <div className="flex items-center justify-center gap-2 h-14 rounded-sm bg-emerald-50 border-2 border-emerald-300">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
              <span className="text-base font-bold text-emerald-700">Job Complete</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Job title + time ── */}
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-xl font-bold text-slate-950 leading-tight" data-testid="job-title">
          {event.job_name || event.description || `Job #${event.job_number || ''}`}
        </h1>
        <div className="flex items-center gap-3 mt-1.5">
          {event.job_number && <span className="text-xs text-slate-400 font-mono">#{event.job_number}</span>}
          {event.job_type && <Badge className="bg-slate-100 text-slate-600 text-[10px]">{event.job_type}</Badge>}
        </div>
        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
          <Clock className="w-4 h-4" strokeWidth={2} />
          <span className="font-medium font-mono">{time}{endTime ? ` — ${endTime}` : ''}</span>
          {event.assigned_tech_name && (
            <span className="flex items-center gap-1 ml-auto text-xs text-slate-400"><User className="w-3 h-3" /> {event.assigned_tech_name}</span>
          )}
        </div>
      </div>

      {/* ── Customer & Communication ── */}
      {cust && (
        <div className="mx-4 mt-2 p-4 bg-white border-2 border-slate-200 rounded-lg" data-testid="customer-section">
          <p className="text-base font-bold text-slate-950">{cust.name}</p>
          {cust.address && (
            <p className="text-sm text-slate-500 flex items-start gap-1.5 mt-1">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" strokeWidth={2} />
              {cust.address}
            </p>
          )}
          <div className="grid grid-cols-3 gap-2 mt-3">
            {cust.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(cust.address)}`} target="_blank" rel="noreferrer"
                className="flex flex-col items-center justify-center gap-1 h-14 rounded-lg bg-blue-600 text-white active:bg-blue-700" data-testid="job-navigate">
                <Navigation className="w-5 h-5" strokeWidth={2.5} /><span className="text-[10px] font-bold uppercase tracking-wide">Navigate</span>
              </a>
            )}
            {cust.phone && (
              <>
                <a href={`tel:${cust.phone}`} className="flex flex-col items-center justify-center gap-1 h-14 rounded-lg bg-slate-100 text-slate-700 active:bg-slate-200" data-testid="job-call">
                  <Phone className="w-5 h-5" strokeWidth={2.5} /><span className="text-[10px] font-bold uppercase tracking-wide">Call</span>
                </a>
                <a href={`sms:${cust.phone}`} className="flex flex-col items-center justify-center gap-1 h-14 rounded-lg bg-slate-100 text-slate-700 active:bg-slate-200" data-testid="job-text">
                  <MessageSquare className="w-5 h-5" strokeWidth={2.5} /><span className="text-[10px] font-bold uppercase tracking-wide">Text</span>
                </a>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Expandable Sections ── */}
      <div className="mt-4">
        <NoteSection
          legacyNotes={event.legacy_notes}
          eventId={eventId}
        />
        <PhotosSection
          attachments={event.attachments || []}
          eventId={eventId}
          onPhotoAdded={(photo) => setEvent(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), photo],
            attachment_count: (prev.attachment_count || 0) + 1,
          }))}
          onCaptionUpdate={(photoId, caption) => setEvent(prev => ({
            ...prev,
            attachments: (prev.attachments || []).map(a =>
              a.id === photoId ? { ...a, caption } : a
            ),
          }))}
        />
        <ChecklistSection
          checklists={event.checklists || []}
          eventId={eventId}
          onChecklistUpdate={(subId, fieldKey, value, scope = null) => {
            setEvent(prev => ({
              ...prev,
              checklists: prev.checklists.map(cl => {
                if (cl.id !== subId) return cl;
                // Phase 3: when scope is provided, update the nested instance
                // inside cl.data; otherwise update the top-level field value.
                if (scope && scope.sectionKey != null && scope.instanceIndex != null) {
                  const data = { ...(cl.data || {}) };
                  const arr = Array.isArray(data[scope.sectionKey]) ? [...data[scope.sectionKey]] : [];
                  while (arr.length <= scope.instanceIndex) arr.push({});
                  arr[scope.instanceIndex] = { ...(arr[scope.instanceIndex] || {}), [fieldKey]: value };
                  data[scope.sectionKey] = arr;
                  return { ...cl, data };
                }
                // Top-level write: keep both the legacy fields[].value and
                // the new cl.data shape in sync.
                const data = { ...(cl.data || {}), [fieldKey]: value };
                return {
                  ...cl,
                  data,
                  fields: cl.fields.map(f => f.key === fieldKey ? { ...f, value } : f),
                };
              }),
            }));
          }}
          onChecklistAttached={(checklist) => {
            setEvent(prev => ({
              ...prev,
              checklists: [...(prev.checklists || []), checklist],
            }));
          }}
        />
        <InvoiceSection
          invoices={invoices}
          eventId={eventId}
          customerPhone={cust?.phone}
          onInvoicesChange={(updater) => setInvoices(updater)}
          onInvoiceCreated={(inv) => {
            setInvoices(prev => [{
              id: inv.id || inv.invoice_id,
              invoice_number: inv.invoice_number,
              total: inv.total,
              balance_due: inv.balance_due,
              status: inv.status || 'draft',
              can_charge_card: false,
              created_at: new Date().toISOString(),
            }, ...prev]);
          }}
        />
        <EstimateSection eventId={eventId} />
        <MaterialsSection
          materials={materials}
          eventId={eventId}
          onMaterialAdded={(mat) => setMaterials(prev => [...prev, mat])}
          onMaterialRemoved={(id) => setMaterials(prev => prev.filter(m => m.id !== id))}
        />
      </div>
    </div>
  );
}
